import { useCallback } from "react"
import { usePackage } from "../package/usePackage"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { trackEvent } from "./events"

type SignupEventPayload = {
  spaceId: number
  createdDate?: string
  type?: string
}

type SignupEventProps = {
  spaceId: number
  createdDate: string
  userId?: number
  packageKey?: string
  type?: string
}

type SignupActivationContext = {
  spaceId?: number
  createdDate?: string
  userId?: number
  packageKey?: string
  type?: string
}

const getCreatedDate = (createdDate?: string) =>
  createdDate || new Date().toISOString()

const getSignupActivationContextKey = (orderId: number) =>
  `signup_activation_context_${orderId}`

export const saveSignupActivationContext = (
  orderId: number,
  context: SignupActivationContext
) => {
  if (typeof window === "undefined" || !orderId) return
  try {
    localStorage.setItem(
      getSignupActivationContextKey(orderId),
      JSON.stringify(context)
    )
  } catch {}
}

export const popSignupActivationContext = (
  orderId: number
): SignupActivationContext | null => {
  if (typeof window === "undefined" || !orderId) return null

  const key = getSignupActivationContextKey(orderId)
  let rawContext: string | null = null
  try {
    rawContext = localStorage.getItem(key)
    localStorage.removeItem(key)
  } catch {
    return null
  }

  if (!rawContext) return null

  try {
    return JSON.parse(rawContext)
  } catch {
    return null
  }
}

export const trackSignupCreateEvents = async (props: SignupEventProps) => {
  await Promise.all([
    trackEvent({
      event: "Signup: Create Space",
      props
    }),
    trackEvent({
      event: "Signup: Create Grok",
      props
    })
  ])
}

export const trackSignupActivateEvents = async (props: SignupEventProps) => {
  await Promise.all([
    trackEvent({
      event: "Signup: Activate",
      props
    }),
    trackEvent({
      event: "Signup: Activate Grok",
      props
    })
  ])
}

export const useSignupEventTracking = () => {
  const { me } = usePathwrightContext()
  const { packageData } = usePackage()

  const getEventProps = useCallback(
    ({ spaceId, createdDate, type }: SignupEventPayload): SignupEventProps => {
      const props: SignupEventProps = {
        spaceId,
        createdDate: getCreatedDate(createdDate)
      }

      if (typeof me?.id === "number") {
        props.userId = me.id
      }

      if (packageData?.key) {
        props.packageKey = packageData.key
      }

      if (type) {
        props.type = type
      }

      return props
    },
    [me?.id, packageData?.key]
  )

  const trackCreateSpaceEvents = useCallback(
    async (payload: SignupEventPayload) => {
      const props = getEventProps(payload)
      await trackSignupCreateEvents(props)
    },
    [getEventProps]
  )

  const savePendingActivateContext = useCallback(
    (orderId: number, payload: SignupEventPayload) => {
      if (!orderId) return
      const props = getEventProps({
        ...payload,
        createdDate: getCreatedDate(payload.createdDate)
      })
      saveSignupActivationContext(orderId, props)
    },
    [getEventProps]
  )

  return {
    trackCreateSpaceEvents,
    savePendingActivateContext
  }
}
